/**
 * Fishbone (Ishikawa): the effect at the head, and its causes along a spine.
 *
 * The root is the *head* — the problem being explained — and it stays where it is, on the right.
 * A spine runs left from it. Each branch of the root is a *bone*: a cause, alternately above and
 * below the spine, joined to it by a slanted line. Everything under a bone is a sub-cause, listed
 * between the bone and the spine, each one ticking off the slanted line at its own height. That
 * is the whole diagram, and it is read the way it is drawn: effect on the right, causes
 * streaming in from the left.
 *
 * The geometry of the slanted line is shared with the renderer through `fishboneSlantX`, so the
 * layout puts a sub-cause's right edge beside the line and the renderer draws the line exactly
 * there. Two copies of that arithmetic would drift, and a tick that stops short of its bone is
 * the first thing anybody notices.
 *
 * Only whole maps: a fishbone is read from its head, and a branch arranged as one would be a
 * second head hanging off the side of something else.
 */
import { childrenOf } from '@orbit-mind/domain';
import { onlyMoves } from './moves';
import { spacingFrom, } from './types';
export const FISHBONE_LAYOUT_ID = 'fishbone/right';
/** Horizontal run of the slanted line per unit of height. 0.5 is a bone at about 63 degrees. */
export const FISHBONE_SLOPE = 0.5;
/** Between the spine and the nearest box above or below it. */
const SPINE_CLEARANCE = 28;
/** Between a bone and the first sub-cause, and between sub-causes. */
const ROW_GAP = 10;
/** Between a sub-cause's right edge and the slanted line it ticks off. */
const TICK_GAP = 16;
/** How much further left each level below a sub-cause sits. */
const INDENT = 24;
/**
 * Where a bone's slanted line is, at height `y`.
 *
 * The line runs from the middle of the bone's edge that faces the spine down (or up) to the
 * spine, leaning right as it goes. Exported because the renderer draws this line and the layout
 * places boxes against it; see the file comment.
 */
export function fishboneSlantX(bone, spineY, y) {
    const above = bone.y + bone.height / 2 < spineY;
    const anchorY = above ? bone.y + bone.height : bone.y;
    return bone.x + bone.width / 2 + Math.abs(y - anchorY) * FISHBONE_SLOPE;
}
export function packFishbone(input) {
    const { document } = input;
    const head = document.nodes[document.map.rootNodeId];
    if (head === undefined)
        return { positions: {} };
    const spacing = spacingFrom(input.spacing);
    const pinned = (node) => node.pinned === true && input.preserveManualPositions;
    const children = (node) => node.collapsed ? [] : childrenOf(document, node.id).filter((child) => !child.hidden);
    const size = (node) => input.extentOf(node.id);
    const headSize = size(head);
    const spineY = head.y + headSize.height / 2;
    // One column per bone: the bone and, depth first, everything under it.
    const columns = children(head).map((bone, index) => {
        const rows = [];
        const walk = (node, depth) => {
            for (const child of children(node)) {
                rows.push({ node: child, depth });
                walk(child, depth + 1);
            }
        };
        walk(bone, 1);
        const rowsHeight = rows.reduce((sum, row) => sum + size(row.node).height + ROW_GAP, 0);
        return { bone, above: index % 2 === 0, rows, rowsHeight };
    });
    const placements = {};
    const place = (node, x, y) => {
        if (!pinned(node))
            placements[node.id] = { x, y };
    };
    /**
     * Lays one column out against a bone centred on `boneCentreX`, and says how far it reaches
     * either side of that centre. Run once to measure and once to place.
     */
    const lay = (column, boneCentreX, commit) => {
        const boneSize = size(column.bone);
        const boneY = column.above
            ? spineY - SPINE_CLEARANCE - column.rowsHeight - boneSize.height
            : spineY + SPINE_CLEARANCE + column.rowsHeight;
        const boneRect = {
            x: boneCentreX - boneSize.width / 2,
            y: boneY,
            width: boneSize.width,
            height: boneSize.height,
        };
        if (commit)
            place(column.bone, boneRect.x, boneRect.y);
        let left = boneRect.x;
        // The slanted line meets the spine here, which is the column's reach to the right.
        let right = Math.max(boneRect.x + boneRect.width, fishboneSlantX(boneRect, spineY, spineY));
        // Rows run from the bone towards the spine above it, and from the spine towards the bone
        // below it; either way they are listed top to bottom in the order they were written.
        let cursor = column.above ? boneRect.y + boneRect.height + ROW_GAP : spineY + SPINE_CLEARANCE;
        for (const row of column.rows) {
            const rowSize = size(row.node);
            const centreY = cursor + rowSize.height / 2;
            const rightEdge = fishboneSlantX(boneRect, spineY, centreY) - TICK_GAP - INDENT * (row.depth - 1);
            const x = rightEdge - rowSize.width;
            if (commit)
                place(row.node, x, cursor);
            left = Math.min(left, x);
            right = Math.max(right, rightEdge);
            cursor += rowSize.height + ROW_GAP;
        }
        return { left: boneCentreX - left, right: right - boneCentreX };
    };
    // Pairs of bones, one above and one below, walking left from the head.
    let cursor = head.x - spacing.rank;
    for (let index = 0; index < columns.length; index += 2) {
        const pair = columns.slice(index, index + 2);
        const reach = pair.map((column) => lay(column, 0, false));
        const rightReach = Math.max(...reach.map((extent) => extent.right));
        const leftReach = Math.max(...reach.map((extent) => extent.left));
        const centreX = cursor - rightReach;
        for (const column of pair)
            lay(column, centreX, true);
        cursor = centreX - leftReach - spacing.rank;
    }
    return { positions: onlyMoves(document, placements) };
}
export const fishboneLayout = {
    id: FISHBONE_LAYOUT_ID,
    name: 'Fishbone',
    run: packFishbone,
};
//# sourceMappingURL=fishbone.js.map